import React from 'react';
import { X, Eye, Calendar, Heart } from 'lucide-react';
import { TagBadge, VideoPlayer } from './ui';
import { CATEGORIES, COLORS } from '@/data';
import { getRedGifsWatchUrl } from '@/lib/utils';
import type { Clip, ScheduledActivity } from '@/types';

interface ClipPreviewPopupProps {
  clip: Clip;
  scheduledActivities?: ScheduledActivity[];
  onClose: () => void;
  onSchedule?: () => void;
}

export const ClipPreviewPopup: React.FC<ClipPreviewPopupProps> = ({
  clip,
  scheduledActivities = [],
  onClose,
  onSchedule,
}) => {
  const category = CATEGORIES.find(c => c.id === clip.categoryId);
  const watchUrl = getRedGifsWatchUrl(clip.link);

  // Count how many times this clip has been scheduled/completed
  const timesScheduled = scheduledActivities.filter(a => a.clipId === clip.id).length;
  const timesCompleted = scheduledActivities.filter(a => a.clipId === clip.id && a.completedAt).length;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-zinc-800 rounded-2xl w-full max-w-lg border border-zinc-700 overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`bg-gradient-to-r ${category?.color || 'from-zinc-600 to-zinc-700'} p-4 flex items-center justify-between`}>
          <div className="flex items-center gap-3">
            <span className="text-3xl">{category?.emoji || '📎'}</span>
            <div>
              <h2 className="text-white font-bold text-lg">{clip.title}</h2>
              <p className="text-white/70 text-sm">{category?.name || 'Unknown'}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg bg-black/20 text-white/80 hover:text-white hover:bg-black/30"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Video Preview */}
        <VideoPlayer url={clip.link} title={clip.title} />

        {/* Outlink */}
        {watchUrl && (
          <div className="bg-zinc-900 px-4 py-2 flex items-center justify-center border-b border-zinc-700">
            <a
              href={watchUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-zinc-400 hover:text-lime-400 text-sm flex items-center gap-2"
            >
              <Eye className="w-4 h-4" /> Open on RedGifs
            </a>
          </div>
        )}

        {/* Details */}
        <div className="p-4 space-y-4">
          {/* Description */}
          {clip.description && (
            <p className="text-zinc-300 text-sm">{clip.description}</p>
          )}

          {/* Tags */}
          {(clip.tags || []).length > 0 && (
            <div className="flex flex-wrap gap-1">
              {(clip.tags || []).map(tag => (
                <TagBadge key={tag} tag={tag} small />
              ))}
            </div>
          )}

          {/* Stats */}
          <div className="flex gap-4 text-sm">
            {timesScheduled > 0 && (
              <div className="flex items-center gap-1.5 text-orange-400">
                <Calendar className="w-4 h-4" />
                <span>Scheduled {timesScheduled}x</span>
              </div>
            )}
            {timesCompleted > 0 && (
              <div className="flex items-center gap-1.5 text-green-400">
                <Heart className="w-4 h-4" />
                <span>Done {timesCompleted}x</span>
              </div>
            )}
            {clip.couplesTried && clip.couplesTried > 0 && (
              <div className="flex items-center gap-1.5 text-pink-400">
                <Heart className="w-4 h-4" />
                <span>{clip.couplesTried} couples tried</span>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            {onSchedule && (
              <button
                onClick={() => { onSchedule(); onClose(); }}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-medium text-zinc-900"
                style={{ backgroundColor: COLORS.lime }}
              >
                <Calendar className="w-5 h-5" /> Schedule This
              </button>
            )}
            <button
              onClick={onClose}
              className="flex-1 px-4 py-3 rounded-xl font-medium bg-zinc-700 text-zinc-300 hover:bg-zinc-600"
            >
              Close
            </button>
          </div>

          {/* Drag hint */}
          <p className="text-center text-xs text-zinc-500">
            Or drag this clip from the sidebar onto a calendar date
          </p>
        </div>
      </div>
    </div>
  );
};
